import { useState, useCallback } from 'react';
import { Modal } from '@eventbrite/marmalade';
import { useIntl } from 'react-intl';
import { messages } from './constants';
import type { ContactView, ContactFormData, ContactFormErrors } from './types';
import { INITIAL_FORM_DATA } from './types';
import { LandingView } from './LandingView';
import { FormView } from './FormView';
import { ConfirmationView } from './ConfirmationView';
import { SuccessView } from './SuccessView';
import styles from './ContactModal.module.scss';

interface Props {
  isShown: boolean;
  onClose: () => void;
  organizerId: string;
}

export function ContactModal({ isShown, onClose, organizerId }: Props) {
  const intl = useIntl();
  const [currentView, setCurrentView] = useState<ContactView>('landing');
  const [formData, setFormData] = useState<ContactFormData>(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState<ContactFormErrors>({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      setCurrentView('landing');
      setFormData(INITIAL_FORM_DATA);
      setFormErrors({});
      setShowValidationBanner(false);
    }, 300);
  }, [onClose]);

  const handleFieldChange = useCallback((field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  return (
    <Modal
      className={styles.contactModalRoot}
      isShown={isShown}
      headerTitle={intl.formatMessage(messages.modalTitle)}
      handleClose={handleClose}
      closeTitle={intl.formatMessage(messages.closeModal)}
    >
      <div className={styles.contactModalBody}>
        {currentView === 'landing' && <LandingView onContactClick={() => setCurrentView('form')} />}

        {currentView === 'form' && (
          <div className={styles.bottomAlignedView}>
            <FormView
              formData={formData}
              formErrors={formErrors}
              showValidationBanner={showValidationBanner}
              onFieldChange={handleFieldChange}
              onBack={() => setCurrentView('landing')}
              onContinue={() => setCurrentView('confirmation')}
              onSetErrors={setFormErrors}
              onSetShowValidationBanner={setShowValidationBanner}
            />
          </div>
        )}

        {currentView === 'confirmation' && (
          <div className={styles.bottomAlignedView}>
            <ConfirmationView
              formData={formData}
              organizerId={organizerId}
              onEdit={() => setCurrentView('form')}
              onSuccess={() => setCurrentView('success')}
            />
          </div>
        )}

        {currentView === 'success' && (
          <div className={styles.bottomAlignedView}>
            <SuccessView onDone={handleClose} />
          </div>
        )}
      </div>
    </Modal>
  );
}
